import { Component, DestroyRef, Input, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ErrorsFormsService } from '@bo-client/shared/form-errors/errors-forms.service';
import { GridRegistrationRequestService } from '@bo-client/activation-management/grid-registration/grid-registration-request.service';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import {
  GridRegistrationModel,
  GridRegistrationStorageV2Form
} from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { GridRegistrationFormBase } from '@bo-client/activation-management/grid-registration/grid-registration-form.base';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import {
  FeedInCompensationType,
  GridRegistrationStatusEnum,
  GridRegistrationTypeEnum
} from '../../../grid-registration/model/grid-registration.enum';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { AreaTypeEnum, CommonStatusEnum } from '../../../shared/models/commissioningModel';
import Utils from '@bo-client/shared/utils';
import {
  ConsumerFormV2,
  ConsumerModel,
  ConsumerTypeEnum
} from '@bo-client/activation-management/grid-registration/model/consumer.model';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import {
  StorageForm,
  StorageModel,
  StorageOperationType,
  StorageOperationTypeEnum
} from '@bo-client/activation-management/grid-registration/model/storage.model';
import { TypedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';
import { ConnectionObjectFormV2 } from '@bo-client/shared/models/connectionObject';
import { PVModuleForm } from '@bo-client/activation-management/grid-registration/model/pv-module.model';
import {
  ProducerForm,
  ProducerModel
} from '@bo-client/activation-management/grid-registration/model/producer.model';
import { ExistingFeederFormV2 } from '@bo-client/activation-management/grid-registration/model/existing-feeder.model';
import { UploadForm } from '@bo-client/shared/models/documentDto';
import { MeteringConceptModel } from '@bo-client/activation-management/shared/models/meteringConceptModel';
import { UiAdaptionsModel } from '@bo-client/tenant-management/models/ui-adaptions.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MeteringConceptDisplayGroup } from '@bo-client/activation-management/grid-registrations/grid-registration-form-v2/form-parts/grid-registration-metering-concept-v2/grid-registration-metering-concept-v2.component';

function containsProduction(op: StorageOperationType | null | undefined): boolean {
  return (
    op === StorageOperationTypeEnum.CONSUMPTION_PRODUCTION ||
    op === StorageOperationTypeEnum.NO_CONSUMPTION_PRODUCTION
  );
}

function containsConsumption(op: StorageOperationType | null | undefined): boolean {
  return (
    op === StorageOperationTypeEnum.CONSUMPTION_NO_PRODUCTION ||
    op === StorageOperationTypeEnum.CONSUMPTION_PRODUCTION
  );
}

@Component({
  selector: 'app-grid-registration-form-storage-v2',
  templateUrl: './grid-registration-form-storage-v2.component.html',
  styleUrls: ['./grid-registration-form-storage-v2.component.scss']
})
export class GridRegistrationFormStorageV2Component extends GridRegistrationFormBase implements OnInit {
  private destroyRef = inject(DestroyRef);

  customConditionsCheckbox = new FormControl(false);
  formGroup: GridRegistrationStorageV2Form;
  @Input() originalModel: GridRegistrationModel;

  private modelState$ = new BehaviorSubject<CommonStatusEnum | null>(null);
  readonly canUpdate$ = of(true);
  readonly connectionObjectData$: Observable<ConnectionObjectFormV2['value']>;
  readonly differingInvoiceContact$: Observable<boolean>;
  readonly directMarketing$: Observable<boolean>;
  readonly disableAll$ = of(false);
  isFullFeedMeteringConcept$: Observable<boolean | null>;
  readonly powerThresholdReached$: Observable<boolean>;
  readonly selectedCompanyLocation$: Observable<CompanyLocationDto>;
  readonly totalConsumerStoragePower$: Observable<number>;
  readonly hasProduction$: Observable<boolean>;
  showFullFeedInBonusProcess$ = this.dataShareService
    .getSingleTenantConfig$<boolean>('feederContract.showFullFeedInBonusProcess', false)
    .pipe(takeUntilDestroyed());
  readonly hideAdditionalGridConnectionPointInformationCard$ = this.dataShareService
    .getSingleTenantConfig$<boolean>(
      'gridRegistration.hideAdditionalGridConnectionPointInformationCard',
      false
    )
    .pipe(takeUntilDestroyed());

  constructor(
    protected authorizationService: AuthorizationService,
    protected createFormsGridRegistrationService: CreateGridRegistrationFormsService,
    protected dataShareService: DataShareService,
    protected errorsFormsService: ErrorsFormsService,
    protected gridRegistrationService: GridRegistrationRequestService,
    protected httpHapService: HttpHapService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected toastService: ToastrService,
    private createFormService: CreateFormsService,
    private fb: FormBuilder,
    private validatorsService: ValidatorsService
  ) {
    super(authorizationService, dataShareService);

    // Placeholder until loadFormWithModel / updateFormWithModel runs; required for createStorageFormV2 /
    // createProducerForm during form construction (this.originalModel was previously undefined).
    this.originalModel = new GridRegistrationModel();
    this.originalModel.gridRegistrationType = GridRegistrationTypeEnum.Storage;

    this.formGroup = this.fb.group(
      {
        state: [null as GridRegistrationModel['state'] | ''],
        formVersion: ['v2' as GridRegistrationModel['formVersion']],
        customer: this.createFormService.createContactDataFormV2({}, true),
        invoiceContact: this.createFormService.createContactDataFormV2({}, false),
        differingInvoiceContact: [false],
        hasDifferentInvoiceContact: [null as GridRegistrationModel['hasDifferentInvoiceContact']],
        connectionObject: this.createFormService.connectionObjectFormWithValidatorV2({}),
        connectionObjectAdditionalInfo: this.createFormService.connectionObjectAdditionalInfoForm({}),

        installerFeedbackComment: [null as GridRegistrationModel['installerFeedbackComment']],

        companyLocation: this.fb.group({ id: [null as number] }),

        comment: [null as GridRegistrationModel['comment']],
        shortDescription: [null as GridRegistrationModel['shortDescription']],
        longDescription: [null as GridRegistrationModel['longDescription']],
        externalId: [null as GridRegistrationModel['externalId']],
        assignedTransformerStation: [null as GridRegistrationModel['assignedTransformerStation']],
        gridConnectionPointId: [null as GridRegistrationModel['gridConnectionPointId']],
        trId: [null as GridRegistrationModel['trId']],
        srId: [null as GridRegistrationModel['srId']],
        meLo: [null as GridRegistrationModel['meLo']],
        maLo: [null as GridRegistrationModel['maLo']],
        meterNumber: [null as GridRegistrationModel['meterNumber']],

        hasInverterInstalled: [false as GridRegistrationModel['hasInverterInstalled']],

        directMarketing: [null as GridRegistrationModel['directMarketing']],
        directMarketer: [null as GridRegistrationModel['directMarketer']],

        feedInCompensationType: [null as GridRegistrationModel['feedInCompensationType']],

        hasStorage: [true as GridRegistrationModel['hasStorage']],
        storages: this.fb.array<StorageForm>([
          this.createFormsGridRegistrationService.createStorageFormV2(
            {
              acCoupling: false,
              alternatingCurrent: false,
              count: null,
              couplingType: undefined,
              currentType: undefined,
              dcCoupling: false,
              deviceTypeName: '',
              disconnectionByIsolatedOperation: false,
              id: null,
              manufacturer: '',
              networkAndDeviceProtection: false,
              networkReplaceService: false,
              phases: null,
              rotaryCurrent: false,
              storageOperationType: null,
              usableCapacity: null,
              formVersion: 2
            },
            this.originalModel,
            false
          )
        ]),
        consumers: this.fb.array<ConsumerFormV2>([]),
        producers: this.fb.array<ProducerForm>([]),

        pvModules: this.fb.array<PVModuleForm>([]),

        meteringConcept: this.fb.group({ id: [null as MeteringConceptModel['id']] }),
        uploads: this.fb.array<UploadForm>([]),

        acceptedConditions1: [
          null as GridRegistrationModel['acceptedConditions1'],
          this.validatorsService.checkboxRequiredValidator()
        ],
        acceptedConditions2: [null as GridRegistrationModel['acceptedConditions2']],

        contractedConnectionPower: [null as GridRegistrationModel['contractedConnectionPower']],
        connectionFuseAmplitude: [null as GridRegistrationModel['connectionFuseAmplitude']],
        // Steuert die Anzeige der bereits vorhandenen Erzeugungsanlagen unter "Weitere Informationen
        // zum Netzanschluss" — analog zur Einspeiser-Netzanmeldung.
        hasInstalledDevices: [null as GridRegistrationModel['hasInstalledDevices']],
        existingFeeders: this.fb.array<ExistingFeederFormV2>([]),

        validityExpirationDate: [null as GridRegistrationModel['validityExpirationDate']],

        externalCustomerId: ['' as GridRegistrationModel['externalCustomerId']],

        fullFeedInBonusRequested: [null as GridRegistrationModel['fullFeedInBonusRequested']]
      },
      {
        updateOn: 'change'
      }
    );

    this.formGroup.controls.storages.valueChanges
      .pipe(
        takeUntilDestroyed(),
        startWith(this.formGroup.controls.storages.value),
        map(() => this.formGroup.controls.storages.at(0)),
        filter((sf): sf is StorageForm => !!sf),
        switchMap((storageForm) =>
          storageForm.controls.storageOperationType.valueChanges.pipe(
            startWith(storageForm.controls.storageOperationType.value)
          )
        ),
        distinctUntilChanged()
      )
      .subscribe((op) => {
        if (!containsProduction(op)) {
          this.formGroup.controls.producers.clear();
        } else if (this.formGroup.controls.producers.length === 0) {
          this.formGroup.controls.producers.push(
            this.createFormsGridRegistrationService.createProducerFormSimple(
              new ProducerModel(),
              this.originalModel
            )
          );
        }

        if (!containsConsumption(op)) {
          this.formGroup.controls.consumers.clear();
        } else if (this.formGroup.controls.consumers.length === 0) {
          this.formGroup.controls.consumers.push(
            this.createFormsGridRegistrationService.createConsumerFormV2(
              new ConsumerModel(ConsumerTypeEnum.Storage)
            )
          );
        }
      });

    this.selectedCompanyLocation$ =
      this.formGroup.controls.connectionObject.controls.orgUnitDesc.valueChanges.pipe(
        startWith(this.formGroup.controls.connectionObject.controls.orgUnitDesc.value),
        combineLatestWith(this.dataShareService.companyLocations$),
        filter(([orgUnitDesc, companyLocations]) => !!orgUnitDesc && !!companyLocations),
        map(([orgUnitDesc, companyLocations]) => {
          return companyLocations.find((x) => x.external_id === orgUnitDesc);
        }),
        shareReplay(1)
      );
    this.selectedCompanyLocation$.subscribe((value) =>
      this.formGroup.controls.companyLocation.controls.id.setValue(value?.id || null)
    );

    // Immer über `getRawValue()`: Darf ein Installateur den Antrag nur lesen, sind alle Controls
    // deaktiviert — `value` und `valueChanges` lassen deaktivierte Controls aus und liefern damit
    // ein Formular *ohne* `storages`. Die daraus abgeleiteten Abschnitte (Wechselrichter,
    // Messkonzept) würden in der Leseansicht fehlen.
    this.hasProduction$ = this.formGroup.valueChanges.pipe(
      takeUntilDestroyed(),
      startWith(null),
      map(() => {
        const firstStorage = this.formGroup.getRawValue().storages?.[0];
        return firstStorage ? StorageModel.hasProduction(firstStorage.storageOperationType) : false;
      })
    );

    this.connectionObjectData$ = this.formGroup.controls.connectionObject.valueChanges.pipe(
      takeUntilDestroyed(),
      startWith(this.formGroup.controls.connectionObject.value),
      shareReplay(1)
    );

    this.isFullFeedMeteringConcept$ = this.formGroup.controls.meteringConcept.controls.id.valueChanges.pipe(
      takeUntilDestroyed(),
      startWith((this.formGroup.getRawValue().meteringConcept?.id as number) || null),
      combineLatestWith(this.dataShareService.meteringConcepts.all$),
      map(([id, meteringConcepts]) => (id ? meteringConcepts.find((concept) => concept.id === id) : null)),
      map((concept) => {
        if (!concept) return null;
        return concept.isFullFeedIn;
      }),
      shareReplay(1)
    );
    this.isFullFeedMeteringConcept$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (value: boolean | null) => {
        if (value === false) {
          this.formGroup.controls.fullFeedInBonusRequested.setValue(false);
        }
      }
    });

    this.differingInvoiceContact$ = this.formGroup.controls.differingInvoiceContact.valueChanges.pipe(
      takeUntilDestroyed(),
      startWith(this.formGroup.controls.differingInvoiceContact.value)
    );

    this.directMarketing$ = this.formGroup.controls.feedInCompensationType.valueChanges.pipe(
      takeUntilDestroyed(),
      startWith(this.formGroup.controls.feedInCompensationType.value),
      map((value) =>
        [
          FeedInCompensationType.MISC_DIRECT_MARKETING,
          FeedInCompensationType.SUBST_DIRECT_MARKETING
        ].includes(value)
      )
    );

    this.totalConsumerStoragePower$ = this.formGroup.valueChanges.pipe(
      takeUntilDestroyed(),
      startWith(null),
      map(() => {
        const formValue = this.formGroup.getRawValue();
        const power = formValue.consumers[0]?.power || 0;
        const count = formValue.storages[0]?.count || 0;
        if (!count || !power) {
          return Number.NaN;
        }
        return Number(power) * Number(count);
      })
    );

    this.powerThresholdReached$ = combineLatest([
      this.hasProduction$,
      this.formGroup.valueChanges.pipe(
        takeUntilDestroyed(),
        startWith(null),
        map(() => this.formGroup.getRawValue().producers ?? [])
      )
    ]).pipe(
      map(([hasProduction, producers]) => {
        if (!hasProduction) return false;
        const totalPower = producers.reduce(
          (sum, p) => sum + (Number(p.acActivePowerMax) || 0) * (Number(p.count) || 0),
          0
        );
        return totalPower > 30;
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );
    this.powerThresholdReached$
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(),
        combineLatestWith(this.dataShareService.uiAdaptions$)
      )
      .subscribe({
        next: ([isReached, adaptions]) => {
          const control = this.formGroup.controls.acceptedConditions2;

          const adaptionVisibility = adaptions.find(
            (x) => x.anchor === 'gridRegistration.powerThresholdConditions_30'
          )?.visibility;
          if (!isReached || !(adaptionVisibility === 'show' || adaptionVisibility === 'true')) {
            control.clearValidators();
            control.markAsUntouched();
            control.setValue(null);
          } else {
            control.setValidators([this.validatorsService.checkboxRequiredValidator()]);
          }
        }
      });
  }

  getFormGroup() {
    return this.formGroup;
  }

  getModelFromForm(): GridRegistrationModel {
    const response = this.formGroup.value;
    const updatedModel = {
      ...this.originalModel,
      ...response
    };

    Utils.logDiff(this.originalModel, updatedModel);

    return updatedModel as GridRegistrationModel;
  }

  loadErrorsList() {}

  isValid() {
    this.formGroup.updateValueAndValidity();
    this.formGroup.markAllAsTouched();
    this.customConditionsCheckbox.markAllAsTouched();
    console.debug('storage form isValid: ', this.formGroup, this.customConditionsCheckbox);
    return (
      (this.formGroup.valid || this.formGroup.disabled) &&
      this.customConditionsCheckbox.valid &&
      this.isConnectionObjectAreaAvailabilityValid()
    );
  }

  loadFormWithModel(model: GridRegistrationModel) {
    this.updateFormWithModelInternal(model);
  }

  ngOnInit(): void {
    combineLatest([this.dataShareService.uiAdaptions$, this.modelState$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([adaptions]) => {
        const checkboxAdaption = adaptions.find(
          (adaption) => adaption.anchor === 'gridRegistration.Storage.conditionsCheckbox'
        );
        this.updateCustomConditionsCheckboxValidation(checkboxAdaption);
      });

    this.formGroup.controls.state.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.formGroup.controls.state.value),
        tap((x) => {
          const isInstallerUser = this.authorizationService.hasOneOfThisRoles([
            'ROLE_INSTALLATEUR',
            'ROLE_ADMIN_INSTALLATEUR'
          ]);

          const isEditorUser = this.authorizationService.hasOneOfThisRoles(['ROLE_EDITOR_GRIDREGISTRATIONS']);
          const canEditAsInstaller =
            isInstallerUser &&
            [GridRegistrationStatusEnum.Incomplete, GridRegistrationStatusEnum.FeedbackNeeded].includes(
              x as CommonStatusEnum
            );
          const canEditAsEditor =
            isEditorUser &&
            [
              GridRegistrationStatusEnum.Requested,
              GridRegistrationStatusEnum.Approved,
              GridRegistrationStatusEnum.UnderReview
            ].includes(x as CommonStatusEnum);

          const canEditAsAnonUser = [
            GridRegistrationStatusEnum.Incomplete,
            GridRegistrationStatusEnum.FeedbackNeeded
          ].includes(x as CommonStatusEnum);

          const canEditAsKeyUser = this.authorizationService.hasOneOfThisRoles([
            'ROLE_KEYUSER_GRIDREGISTRATION'
          ]);
          const canEdit = canEditAsEditor || canEditAsInstaller || canEditAsKeyUser || canEditAsAnonUser;

          if (!canEdit) {
            this.disableFields();
          } else {
            this.enableFields();
          }
        })
      )
      .subscribe();

    // Storage und Consumer ist die gleiche Anlage, weswegen die Daten zwischen den beiden Forms synchronisiert werden müssen
    this.formGroup.controls.storages.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(null),
        map(() => this.formGroup.controls.storages.getRawValue()[0])
      )
      .subscribe({
        next: (storage) => {
          const consumer = this.formGroup.controls.consumers.at(0) as TypedForm<ConsumerModel> | null;
          if (!consumer || !storage || storage.count === null) return;
          consumer.controls.count.patchValue(storage.count);
          consumer.controls.deviceTypeName.patchValue(storage.deviceTypeName);
          consumer.controls.manufacturer.patchValue(storage.manufacturer);
        }
      });
    console.log('storage form', this.formGroup.controls.storages);
  }

  resetDifferingInvoiceContactForm(checked: boolean) {
    if (!checked) {
      for (const control of Object.values(this.formGroup.controls.invoiceContact.controls)) {
        control.clearValidators();
        control.patchValue(null);
      }
    }
  }

  updateFormWithModel(model: GridRegistrationModel) {
    this.updateFormWithModelInternal(model);
  }

  private createProducerForm(gridRegistration: GridRegistrationModel): ProducerForm {
    return this.createFormsGridRegistrationService.createProducerFormSimple(
      !gridRegistration.producers.length ? new ProducerModel() : gridRegistration.producers[0],
      gridRegistration
    );
  }

  private updateCustomConditionsCheckboxValidation(checkboxAdaption: UiAdaptionsModel | undefined): void {
    if (
      !this.customConditionsCheckbox.disabled &&
      checkboxAdaption &&
      (checkboxAdaption.visibility === 'show' || checkboxAdaption.visibility === 'true')
    ) {
      this.customConditionsCheckbox.setValidators(this.validatorsService.checkboxRequiredValidator());
    } else {
      this.customConditionsCheckbox.clearValidators();
    }
    if (
      this.originalModel?.state !== CommonStatusEnum.Incomplete &&
      this.originalModel?.state !== CommonStatusEnum.FeedbackNeeded
    ) {
      this.customConditionsCheckbox.clearValidators();
      this.customConditionsCheckbox.setValue(true);
    }
    this.customConditionsCheckbox.updateValueAndValidity();
  }

  private updateFormWithModelInternal(model: GridRegistrationModel) {
    this.originalModel = model;

    this.formGroup.controls.storages.clear();
    const storageFromModel = model.storages[0];
    this.formGroup.controls.storages.push(
      this.createFormsGridRegistrationService.createStorageFormV2(
        storageFromModel ?? {
          acCoupling: false,
          alternatingCurrent: false,
          count: null,
          couplingType: undefined,
          currentType: undefined,
          dcCoupling: false,
          deviceTypeName: '',
          disconnectionByIsolatedOperation: false,
          id: null,
          manufacturer: '',
          networkAndDeviceProtection: false,
          networkReplaceService: false,
          phases: null,
          rotaryCurrent: false,
          storageOperationType: null,
          usableCapacity: null,
          formVersion: 2
        },
        model,
        false
      )
    );

    this.formGroup.controls.consumers.clear();
    const consumerFromModel = model.consumers[0];
    const storageOp = storageFromModel?.storageOperationType;
    if (containsConsumption(storageOp)) {
      this.formGroup.controls.consumers.push(
        this.createFormsGridRegistrationService.createConsumerFormV2(
          consumerFromModel ?? new ConsumerModel(ConsumerTypeEnum.Storage)
        )
      );
    }

    this.formGroup.controls.producers.clear();
    if (containsProduction(storageOp)) {
      if (model.producers?.length) {
        model.producers.forEach((p) =>
          this.formGroup.controls.producers.push(
            this.createFormsGridRegistrationService.createProducerFormSimple(p, model)
          )
        );
      } else {
        this.formGroup.controls.producers.push(this.createProducerForm(model));
      }
    }

    this.formGroup.controls.uploads.clear();
    model.uploads.forEach((upload) => {
      const uploadFormGroup = this.createFormService.createUploadForm(upload);
      this.formGroup.controls.uploads.push(uploadFormGroup);
    });

    this.formGroup.controls.existingFeeders.clear();
    model.existingFeeders?.forEach((existingFeeder) => {
      this.formGroup.controls.existingFeeders.push(
        this.createFormsGridRegistrationService.createExistingFeederFormV2(existingFeeder)
      );
    });

    this.formGroup.patchValue(model as GridRegistrationStorageV2Form['value'], { emitEvent: true });

    // Antrag mit gespeicherten Erzeugungsanlagen: Die Checkbox muss die Liste widerspiegeln, auch
    // wenn `hasInstalledDevices` am Antrag (noch) nicht gesetzt ist.
    if (this.formGroup.controls.existingFeeders.length > 0) {
      this.formGroup.controls.hasInstalledDevices.patchValue(true);
    }

    this.formGroup.controls.differingInvoiceContact.patchValue(!!model.invoiceContact?.salutation);

    this.formGroup.controls.validityExpirationDate.patchValue(
      model.validityExpirationDate ? new Date(model.validityExpirationDate) : null
    );

    // `patchValue` schreibt die ISO-Strings der API zurück in die Formulare — der Datepicker der
    // vorhandenen Erzeugungsanlagen braucht aber ein `Date`.
    this.formGroup.controls.existingFeeders.controls.forEach((existingFeederForm, index) => {
      const commissioningDate = model.existingFeeders?.[index]?.commissioningDate;
      existingFeederForm.controls.commissioningDate.patchValue(
        commissioningDate ? new Date(commissioningDate) : null
      );
    });
    this.modelState$.next(model?.state as CommonStatusEnum);
  }

  readonly GridRegistrationStatusEnum = GridRegistrationStatusEnum;
  readonly GridRegistrationTypeEnum = GridRegistrationTypeEnum;
  protected readonly AreaTypeEnum = AreaTypeEnum;
  protected readonly MeteringConceptDisplayGroup = MeteringConceptDisplayGroup;
}
